import { useEffect, useRef } from 'react';
import { Hand, MicOff } from 'lucide-react';

const playable = (t) => (t && t.state === 'playable' ? (t.persistentTrack || t.track) : null);
export const vTrack = (p) => playable(p?.tracks?.video);
export const sTrack = (p) => playable(p?.tracks?.screenVideo);
export const aTrack = (p) => playable(p?.tracks?.audio);
export const speakerSupported = () => typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

// Raised hands in the order they went up → Map(session_id → 1, 2, …).
export function handOrder(participants) {
  const up = Object.values(participants || {}).filter((p) => p?.session_id && Number(p?.userData?.hand) > 0)
    .sort((a, b) => a.userData.hand - b.userData.hand);
  return new Map(up.map((p, i) => [p.session_id, i + 1]));
}

// Attaches a MediaStreamTrack to a <video>/<audio> element (works in the pop-out window's document too); audio
// plays through the chosen speaker where the browser allows it.
export function MediaEl({ track, kind = 'video', muted, mirror, className, style, sinkId }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = track ? new MediaStream([track]) : null;
    if (track) el.play?.().catch(() => {});
  }, [track]);
  useEffect(() => {
    const el = ref.current;
    if (el && sinkId && el.setSinkId && el.sinkId !== sinkId) el.setSinkId(sinkId).catch(() => {});
  }, [sinkId]);
  if (kind === 'audio') return <audio ref={ref} autoPlay playsInline />;
  return (
    <video ref={ref} autoPlay playsInline muted={muted} className={className}
      style={{ ...(mirror ? { transform: 'scaleX(-1)' } : {}), ...style }} />
  );
}

export function Tile({ p, isLocal, speaking, hand, fill, small }) {
  const v = vTrack(p);
  const name = p?.user_name || (isLocal ? 'You' : 'Guest');
  return (
    <div className={`relative flex items-center justify-center overflow-hidden rounded-xl bg-[#14263B] transition-shadow duration-200 ${speaking ? 'ring-[3px] ring-[#22C55E]' : 'ring-1 ring-white/5'} ${fill ? 'h-full w-full' : ''}`}
      style={fill ? undefined : { aspectRatio: '16 / 9' }} data-testid={`mm-tile-${isLocal ? 'local' : p?.session_id}`}>
      {v ? <MediaEl track={v} muted mirror={isLocal} className={`h-full w-full ${fill && !small ? 'object-contain' : 'object-cover'}`} /> : (
        <div className={`flex items-center justify-center rounded-full bg-[#4575A8] font-semibold text-white ${small ? 'h-9 w-9 text-sm' : 'h-16 w-16 text-2xl'}`}>
          {(name || '?').slice(0, 1).toUpperCase()}
        </div>
      )}
      {hand && (
        <div className="mm-hand-pop absolute right-2 top-2 flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-bold text-black shadow"
          data-testid={`mm-tile-hand-${isLocal ? 'local' : p?.session_id}`}>
          <Hand className="h-3.5 w-3.5" />{hand}
        </div>
      )}
      <div className="absolute bottom-2 left-2 flex max-w-[85%] items-center gap-1 rounded-md bg-black/60 px-2 py-0.5 text-[11px] text-white backdrop-blur-md">
        {!p?.audio && <MicOff className="h-3 w-3 shrink-0 text-red-400" />}
        <span className="truncate">{isLocal ? `${name} (you)` : name}</span>
      </div>
    </div>
  );
}

// A short two-note chime through the chosen speaker, to check people will hear the meeting.
let testUrl;
function chimeUrl() {
  if (testUrl) return testUrl;
  const rate = 22050;
  const n = Math.round(rate * 0.9);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const str = (o, s) => [...s].forEach((ch, i) => v.setUint8(o + i, ch.charCodeAt(0)));
  str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i += 1) {
    const t = i / rate;
    const f = t < 0.4 ? 659.25 : 987.77;
    const local = t < 0.4 ? t : t - 0.4;
    const env = Math.min(1, local * 40) * Math.exp(-local * 6);
    v.setInt16(44 + i * 2, Math.sin(2 * Math.PI * f * t) * env * 0.5 * 32767, true);
  }
  testUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
  return testUrl;
}

export async function playTestSound(sinkId) {
  const a = new Audio(chimeUrl());
  if (sinkId && a.setSinkId) await a.setSinkId(sinkId).catch(() => {});
  await a.play();
  await new Promise((res) => { a.onended = res; setTimeout(res, 1500); });
}
